import { v4 as uuid } from "uuid";
import { useEffect, useState } from "react";
import { DataSheetGrid } from "react-datasheet-grid";
import "react-datasheet-grid/dist/style.css";

import "./styles.css";
import {
  EmployeeLineItem,
  EmployeeSocialProcurementFactor,
  EmploymentType,
  Gender,
  NewExistingJobType,
  OccupationType,
  createDefaultEmployee,
} from "@interfaces/employee";
import { Operation } from "react-datasheet-grid/dist/types";
import { ProjectLineItem } from "@interfaces/project";
import { OrganisationLineItem } from "@interfaces/organisation";
import { validatePostCode } from "@utils/location";
import { Button, Grid, Typography } from "@mui/material";
import { StyledLoadingButton } from "@stories/atoms/StyledButton/StyledButton";
import { getEmployeeColumns } from "./EmployeeColumns";

export interface EmployeeTableRow {
  id: string;
  employeeName?: string;
  gender?: Gender;
  postCode?: string;
  country: string;
  primaryEmployer?: string;
  occupation?: string;
  occupationType?: OccupationType;
  newExistingJob?: NewExistingJobType;
  employmentType?: EmploymentType;
  dirty: boolean;
  invalid?: boolean;
  empty?: boolean;
  socialFactors: Record<string, boolean>;
}

interface EmployeeSheetProps {
  loading: boolean;
  project: ProjectLineItem;
  contractor: OrganisationLineItem;
  employees: EmployeeLineItem[];
  handleUpsertEmployees: (employees: EmployeeLineItem[]) => void;
  handleDeleteEmployees: (employees: EmployeeLineItem[]) => void;
}

export const EmployeeSheet = ({
  loading,
  project,
  contractor,
  employees,
  handleUpsertEmployees,
  handleDeleteEmployees,
}: EmployeeSheetProps) => {
  const [data, setData] = useState<EmployeeTableRow[]>([]);

  if (data.length < 10) {
    for (let i = 0; i < 10 - data.length; i++) {
      data.push({ id: uuid(), empty: true } as EmployeeTableRow);
    }
    setData(data);
  } else {
    if (data.length > 0) {
      if (!data[data.length - 1].empty) {
        data.push({ id: uuid(), empty: true } as EmployeeTableRow);
      }
    }
  }

  const columns = getEmployeeColumns(false);

  const regenData = () => {
    const employeeRowItems = employees.map((e) => {
      const newTableRow = {
        id: e.id,
        employeeName: e.employeeName,
        gender: e.gender,
        postCode: e.postCode,
        country: e.country,
        primaryEmployer: e.primaryEmployer,
        occupation: e.occupation,
        occupationType: e.occupationType,
        newExistingJob: e.newExistingJob,
        employmentType: e.employmentType,
        socialFactors: {},
        dirty: false,
        invalid: false,
        empty: false,
      } as EmployeeTableRow;
      for (const sf of Object.values(EmployeeSocialProcurementFactor)) {
        (newTableRow as unknown as any)[sf] = e.socialFactors.includes(sf);
      }
      return newTableRow;
    });
    setData(employeeRowItems);
  };

  useEffect(() => {
    regenData();
  }, [employees]);

  const validateEmployeeTableRow = (employee: EmployeeTableRow) => {
    const validPostCode = employee.postCode
      ? validatePostCode(project.siteCountry, employee.postCode)
      : true;
    return (
      !employee.employeeName ||
      !employee.employmentType ||
      !employee.gender ||
      !employee.newExistingJob ||
      !validPostCode ||
      !employee.primaryEmployer ||
      !employee.occupation ||
      !employee.occupationType
    );
  };

  const isEmptyEmployeeTableRow = (employee: EmployeeTableRow) => {
    return (
      !employee.employeeName &&
      !employee.employmentType &&
      !employee.gender &&
      !employee.newExistingJob &&
      !employee.postCode &&
      !employee.primaryEmployer &&
      !employee.occupation &&
      !employee.occupationType
    );
  };

  const [deletedRowIds, setDeletedRowIds] = useState(new Set<string>());
  const [updatedRowIds, setUpdatedRowIds] = useState(new Set<string>());

  const onDataChange = (data: EmployeeTableRow[], operations: Operation[]) => {
    data.forEach((d) => {
      d.empty = isEmptyEmployeeTableRow(d);
      d.invalid = validateEmployeeTableRow(d);
    });

    for (const operation of operations) {
      if (operation.type === "CREATE" || operation.type === "UPDATE") {
        data
          .slice(operation.fromRowIndex, operation.toRowIndex)
          .forEach((d) => {
            d.dirty = true;
            setUpdatedRowIds((prev) => {
              if (d.invalid) {
                return prev;
              }
              return new Set(prev.add(d.id));
            });
          });
      }
      if (operation.type === "DELETE") {
        const tableIds = data.map((d) => d.id);
        data
          .slice(operation.fromRowIndex, operation.toRowIndex)
          .forEach((d) => {
            setDeletedRowIds((prev) => {
              const idsToDelete = employees
                .filter((lh) => !tableIds.includes(lh.id))
                .map((lh) => lh.id);
              for (const id of idsToDelete) {
                prev = prev.add(id);
              }
              return new Set(prev);
            });
          });
      }
    }
    setData(data);
  };

  const handleSave = async () => {
    const employeesToUpsert = data.filter(
      (r) => r.id && updatedRowIds.has(r.id)
    );
    const validRows = employeesToUpsert.filter((r) => !r.invalid && !r.empty);

    const employeeLineItemsToUpsert: EmployeeLineItem[] = [];
    validRows.forEach((e) => {
      const newSocialFactors = [];
      for (const sf of Object.values(EmployeeSocialProcurementFactor)) {
        if ((e as unknown as any)[sf]) {
          newSocialFactors.push(sf);
        }
      }

      const originalEmployee = employees.find((o) => o.id === e.id);
      if (originalEmployee) {
        originalEmployee.employeeName =
          e.employeeName || originalEmployee.employeeName;
        originalEmployee.gender = e.gender || originalEmployee.gender;
        originalEmployee.postCode = e.postCode || originalEmployee.postCode;
        originalEmployee.country = e.country || originalEmployee.country;
        originalEmployee.primaryEmployer =
          e.primaryEmployer || originalEmployee.primaryEmployer;
        originalEmployee.occupation =
          e.occupation || originalEmployee.occupation;
        originalEmployee.occupationType =
          e.occupationType || originalEmployee.occupationType;
        originalEmployee.newExistingJob =
          e.newExistingJob || originalEmployee.newExistingJob;
        originalEmployee.employmentType =
          e.employmentType || originalEmployee.employmentType;

        originalEmployee.socialFactors =
          newSocialFactors as EmployeeSocialProcurementFactor[];

        employeeLineItemsToUpsert.push(originalEmployee);
      } else {
        const defualtEmployee = createDefaultEmployee(contractor);
        employeeLineItemsToUpsert.push({
          ...defualtEmployee,
          id: e.id,
          employeeName: e.employeeName || defualtEmployee.employeeName,
          gender: e.gender || defualtEmployee.gender,
          postCode: e.postCode || defualtEmployee.postCode,
          country: e.country || defualtEmployee.country,
          primaryEmployer: e.primaryEmployer || defualtEmployee.primaryEmployer,
          occupation: e.occupation || defualtEmployee.occupation,
          occupationType: e.occupationType || defualtEmployee.occupationType,
          newExistingJob: e.newExistingJob || defualtEmployee.newExistingJob,
          employmentType: e.employmentType || defualtEmployee.employmentType,
          socialFactors: newSocialFactors as EmployeeSocialProcurementFactor[],
        });
      }
    });
    if (employeeLineItemsToUpsert.length > 0) {
      await handleUpsertEmployees(employeeLineItemsToUpsert);
    }

    const employeesToDelete = employees.filter((r) => deletedRowIds.has(r.id));
    if (employeesToDelete.length > 0) {
      await handleDeleteEmployees(employeesToDelete);
    }
    setDeletedRowIds(new Set());
    setUpdatedRowIds(new Set());
  };
  return (
    <>
      <Grid container spacing={2} sx={{ padding: "10px" }}>
        <Grid item xs={12} md={12}>
          <DataSheetGrid<EmployeeTableRow>
            autoAddRow
            value={data}
            onChange={onDataChange}
            columns={columns}
            // rowClassName={({ rowIndex }) => {
            //   return rowIndex % 2 === 0 ? "row-warning" : "";
            // }}
            cellClassName={({ rowData, rowIndex }) => {
              const row = rowData as EmployeeTableRow;
              if (row.empty) {
                return "";
              }
              if (row.invalid) {
                return "row-error";
              }
              return row.dirty ? "row-warning" : "";
            }}
          />
        </Grid>
        <Grid
          container
          alignItems="center"
          justifyContent="flex-end"
          spacing={2}
          mt={2}
        >
          <Grid item>
            <StyledLoadingButton
              onClick={handleSave}
              loading={loading}
              disabled={loading}
            >
              Submit
            </StyledLoadingButton>
          </Grid>
          <Grid item>
            <Typography variant="body2" sx={{ m: 0 }}>
              or
            </Typography>
          </Grid>
          <Grid item>
            <Button variant="text" onClick={regenData}>
              Reset
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
